import {
  BUILTIN_CREATE_ACTION,
  SectionConfigMetadata,
  SectionEditorMetadata,
  TopAction,
  TopBarMetadata
} from '../config-transpiler/models';
import { ColumnConfiguration } from '../shared/column-configurator/column-configurator.component';
import { SectionPayloadParams } from './section-payload.generator';

export interface SectionEditorState {
  label: string;
  path: string;
  entityName: string;
  columns: ColumnConfiguration[];
  linkedCardPath: string | null;
  allowCreate: boolean;
  allowDelete: boolean;
  topActions: TopAction[];
  tableSize: 's' | 'm' | 'l';
  showManualFilter: boolean;
  showFilterPresets: boolean;
  importLegacyFolders: boolean;
  topBar: TopBarMetadata | undefined;
  config: SectionConfigMetadata | undefined;
}

export function buildSectionEditorMetadata(
  state: SectionEditorState,
  saved: SectionEditorMetadata | null | undefined
): SectionEditorMetadata {
  const { linkedCardPath, topActions, showFilterPresets, topBar } = state;
  return {
    schemaVersion: 2,
    label: state.label,
    path: state.path,
    entityName: state.entityName,
    columns: state.columns,
    linkedCardPath: linkedCardPath ?? undefined,
    allowCreate: linkedCardPath ? state.allowCreate : undefined,
    allowDelete: state.allowDelete || undefined,
    topActions: topActions.length > 0 && !topBar ? topActions : undefined,
    tableSize: state.tableSize,
    showManualFilter: state.showManualFilter || undefined,
    showFilterPresets: showFilterPresets || undefined,
    importLegacyFolders:
      (showFilterPresets && state.importLegacyFolders) || undefined,
    prelude: saved?.prelude,
    rootProps: saved?.rootProps,
    rootOrder: saved?.rootOrder,
    config: state.config,
    topBar,
    origin: saved?.origin
  };
}

export function sectionParamsFromMetadata(
  metadata: SectionEditorMetadata
): SectionPayloadParams {
  return {
    label: metadata.label,
    entityName: metadata.entityName,
    columns: metadata.columns ?? [],
    linkedCardPath: metadata.linkedCardPath ?? null,
    allowCreate: metadata.allowCreate ?? false,
    allowDelete: metadata.allowDelete ?? false,
    topActions: metadata.topActions,
    tableSize: metadata.tableSize,
    showManualFilter: metadata.showManualFilter,
    showFilterPresets: metadata.showFilterPresets,
    importLegacyFolders: metadata.importLegacyFolders,
    prelude: metadata.prelude,
    rootProps: metadata.rootProps,
    rootOrder: metadata.rootOrder,
    config: metadata.config,
    topBar: metadata.topBar
  };
}

export function reconcileCreateAction(
  actions: TopAction[],
  allowCreate: boolean,
  linkedCardPath: string | null
): TopAction[] {
  const hasCreate = actions.some((a) => a.id === '__create__');
  const shouldHaveCreate = allowCreate && !!linkedCardPath;
  if (shouldHaveCreate && !hasCreate) {
    return [BUILTIN_CREATE_ACTION, ...actions];
  }
  if (!shouldHaveCreate && hasCreate) {
    return actions.filter((a) => a.id !== '__create__');
  }
  return actions;
}

export function hasSectionEditorChanges(
  current: SectionEditorState,
  initial: SectionEditorState
): boolean {
  return (
    current.label !== initial.label ||
    current.path !== initial.path ||
    (current.entityName !== '' && current.entityName !== initial.entityName) ||
    current.linkedCardPath !== initial.linkedCardPath ||
    current.allowCreate !== initial.allowCreate ||
    current.allowDelete !== initial.allowDelete ||
    current.tableSize !== initial.tableSize ||
    current.showManualFilter !== initial.showManualFilter ||
    current.showFilterPresets !== initial.showFilterPresets ||
    current.importLegacyFolders !== initial.importLegacyFolders ||
    JSON.stringify(current.columns) !== JSON.stringify(initial.columns) ||
    JSON.stringify(current.topActions) !== JSON.stringify(initial.topActions) ||
    JSON.stringify(current.topBar) !== JSON.stringify(initial.topBar) ||
    JSON.stringify(current.config) !== JSON.stringify(initial.config)
  );
}
